import React, { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useLazyQuery } from "@apollo/client";
import { GET_USER_ADDRESS_BY_PHONE } from "../../../graphql/userConfirm/userAddress/query";

// orderGroup ບໍ່ໄດ້ເກັບເບີໄວ້ຈົນກວ່າຈະຢືນຢັນອໍເດີ ຖ້າລູກຄ້າ refresh ກາງທາງຈະຕ້ອງພິມເບີຄືນ
// ຈຶ່ງຈື່ເບີລ່າສຸດໄວ້ໃນເຄື່ອງ. ຫຸ້ມ try/catch ເພາະ webview ຂອງ Facebook ບາງເຄື່ອງປິດ localStorage
const LAST_PHONE_KEY = "CF_LAST_RECEIVER_PHONE";

const readLastPhone = () => {
  try {
    return localStorage.getItem(LAST_PHONE_KEY) || "";
  } catch (err) {
    return "";
  }
};

const saveLastPhone = (value) => {
  try {
    localStorage.setItem(LAST_PHONE_KEY, value);
  } catch (err) {
    // ບໍ່ຈື່ໄດ້ກໍ່ບໍ່ເປັນຫຍັງ ລູກຄ້າພິມເອງໄດ້
  }
};

// ຊ່ອງເບີໂທ inline ແທນ modal ຢືນຢັນຕົວຕົນເກົ່າ:
// ພໍລູກຄ້າພິມຄົບ 8 ຕົວ ລະບົບຄົ້ນທີ່ຢູ່ຂົນສົ່ງໃຫ້ເອງ ບໍ່ຕ້ອງກົດປຸ່ມ ບໍ່ຕ້ອງມີລະຫັດ
// customerName / setCustomerName / setParentPhone ເປັນ optional
// ບ່ອນທີ່ບໍ່ສົ່ງມາ (MultiStepForm) ຈະບໍ່ເຫັນຊ່ອງຊື່ ແລະ ໜ້າຕາຄືເກົ່າທຸກຢ່າງ
function FindAddressByPhone({
  defaultPhone,
  onFound,
  customerName,
  setCustomerName,
  setParentPhone,
}) {
  // ຮັບໄດ້ເຖິງ 11 ຕົວ (ຄືກັບຊ່ອງເບີເກົ່າຂອງໂໝດພິມເອງ) ແຕ່ຈະຄົ້ນຫາສະເພາະຕອນຄົບ 8 ຕົວ
  // ຖ້າຕັດແຄ່ 8 ໂໝດພິມເອງຈະໃສ່ເບີບ້ານ ຫຼື ເບີ 9-11 ຕົວບໍ່ໄດ້ອີກ
  const [phone, setPhone] = useState(
    defaultPhone ? String(defaultPhone).replace(/\D/g, "").slice(-8) : "",
  );

  // ລູກຄ້າແກ້ເບີເອງແລ້ວ ຢ່າເອົາເບີຈາກໜ້າແມ່ມາທັບ (ລຶບເບີອອກໝົດກໍ່ຕ້ອງລຶບໄດ້)
  const touched = useRef(false);

  useEffect(() => {
    if (defaultPhone && !touched.current) {
      setPhone(String(defaultPhone).replace(/\D/g, "").slice(-8));
    }
  }, [defaultPhone]);

  // ສົ່ງເບີຂຶ້ນໄປໃຫ້ໜ້າແມ່ ເພາະຕອນກົດຢືນຢັນອໍເດີມັນໃຊ້ຄ່າຈາກໜ້າແມ່
  // ບໍ່ດັ່ງນັ້ນໂໝດພິມທີ່ຢູ່ເອງຈະຕິດ "ກະລຸນາປ້ອນເບີໂທກ່ອນ" ທັງທີ່ພິມແລ້ວ
  // ຊ່ອງຍັງວ່າງຕອນ mount ຢ່າສົ່ງ "" ຂຶ້ນໄປລຶບເບີທີ່ໜ້າແມ່ມີຢູ່ແລ້ວ
  useEffect(() => {
    if (setParentPhone && (phone || touched.current)) setParentPhone(phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);
  const [isEmptyResult, setIsEmptyResult] = useState(false);
  const debounceRef = useRef(null);

  const [findAddress, { loading, error }] = useLazyQuery(
    GET_USER_ADDRESS_BY_PHONE,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const addresses = data?.userAddressByPhone?.data ?? [];
        setIsEmptyResult(addresses.length === 0);
        // ຈື່ສະເພາະເບີທີ່ຄົ້ນຜ່ານແລ້ວ ບໍ່ຈື່ເບີທີ່ພິມຄ້າງໆ
        saveLastPhone(phone);
        onFound({
          phone,
          userId: data?.userAddressByPhone?.userId ?? null,
          addresses,
        });
      },
    },
  );

  const search = (value) =>
    findAddress({ variables: { where: { userTel: value } } });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (phone.length !== 8) {
      setIsEmptyResult(false);
      return;
    }
    debounceRef.current = setTimeout(() => search(phone), 400);
    return () => clearTimeout(debounceRef.current);
  }, [phone]);

  const onChangePhone = (e) => {
    touched.current = true;
    setPhone(e.target.value.replace(/\D/g, "").slice(0, 11));
  };

  return (
    <div className="cf-card cf-field">
      {/* ຊື່ມາກ່ອນເບີ — ຄົນຕອບຄຳຖາມ "ເຈົ້າແມ່ນໃຜ" ກ່ອນ "ຕິດຕໍ່ໄດ້ບ່ອນໃດ"
          ພໍຄົ້ນເບີພົບແລ້ວ ຊ່ອງນີ້ຈະຖືກຕື່ມຊື່ຈາກທີ່ຢູ່ໃຫ້ເອງ (ຜ່ານ setCustomerName
          ຂອງໜ້າແມ່) ລູກຄ້າແກ້ທັບໄດ້ຖ້າຢາກໃສ່ຊື່ຜູ້ຮັບຄົນອື່ນ */}
      {setCustomerName && (
        <>
          <label className="cf-field__label" htmlFor="cf-receiver-name">
            ຊື່ຜູ້ຮັບເຄື່ອງ <span className="cf-field__required">*</span>
          </label>
          <input
            id="cf-receiver-name"
            className="cf-input"
            type="text"
            value={customerName ?? ""}
            onChange={(e) => setCustomerName(e?.target?.value)}
            placeholder="ຊື່ ແລະ ນາມສະກຸນ"
            autoComplete="name"
            style={{ marginBottom: 12 }}
          />
        </>
      )}

      <label className="cf-field__label" htmlFor="cf-receiver-phone">
        ເບີໂທຜູ້ຮັບເຄື່ອງ <span className="cf-field__required">*</span>
      </label>
      <div style={{ position: "relative" }}>
        <input
          id="cf-receiver-phone"
          // ຂອບແດງກະພິບຈົນກວ່າຈະໃສ່ເບີຄົບ 8 ຕົວ
          className={`cf-input${phone.length < 8 ? " cf-input--attention" : ""}`}
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={onChangePhone}
          placeholder="ໃສ່ເບີໂທລະສັບ 8 ຕົວເລກ"
          autoComplete="tel"
        />
        {loading && (
          <Spinner
            animation="border"
            size="sm"
            style={{
              position: "absolute",
              right: 14,
              top: 13,
              color: "var(--cf-primary)",
            }}
          />
        )}
      </div>

      {phone.length > 0 && phone.length < 8 && (
        <div className="cf-field__helper">
          ໃສ່ໃຫ້ຄົບ 8 ຕົວເລກ (ບໍ່ໃສ່ 020 ຫລື 20 ດ້ານໜ້າ)
        </div>
      )}

      {error && (
        <div className="cf-field__error">
          ຄົ້ນຫາບໍ່ສຳເລັດ{" "}
          <Button
            variant="link"
            size="sm"
            style={{ padding: 0, fontSize: 12 }}
            onClick={() => search(phone)}
          >
            ລອງໃໝ່
          </Button>
        </div>
      )}

      {isEmptyResult && !loading && !error && (
        <div className="cf-field__helper">
          ບໍ່ພົບທີ່ຢູ່ເກົ່າ ກະລຸນາເພີ່ມທີ່ຢູ່ໃໝ່ດ້ານລຸ່ມ
        </div>
      )}
    </div>
  );
}

export default FindAddressByPhone;
